import { useEffect, useRef, useState } from 'react';
import { api, type Memo, type Proposal } from '../lib/api';

// The director's rail (T229): on every bench, what the assistant director
// proposes — this bench's first, the rest after, each a step to the bench it
// belongs to. Read from the workspace's own counts, so it is instant and
// never wrong about what exists; the creator decides, the rail never acts.
export function DirectorRail({ view, onGo, onItems }: { view: string; onGo: (bench: string, move: string) => void; onItems?: (items: Proposal[]) => void }) {
  const [items, setItems] = useState<Proposal[] | null>(null);
  const [memo, setMemo] = useState<Memo | null>(null);
  const [open, setOpen] = useState(true);
  const at = useRef(0);
  useEffect(() => {
    // Once a minute at most: a view change inside the minute reuses what was read.
    if (Date.now() - at.current < 60_000) return;
    at.current = Date.now();
    api.directorNext().then((r) => {
      setItems(r.proposals); onItems?.(r.proposals); setMemo(r.memo);
      // T237: the memo is written once a day; a stale one is asked for again (an editor's move — anyone else keeps the last).
      if (!r.memo || !r.memo.fresh) api.directorMemo().then(setMemo).catch(() => undefined);
    }).catch(() => setItems([]));
  }, [view]);
  if (!items || (items.length === 0 && !memo)) return null;
  const here = items.filter((p) => p.bench === view), there = items.filter((p) => p.bench !== view);
  return (
    <aside data-tool="director-rail" style={{ display: 'grid', gap: 6, border: '1px solid var(--line)', borderRadius: 10, padding: '8px 12px', marginBottom: 14, background: 'var(--surface-2, transparent)' }}>
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <strong style={{ fontSize: 12.5 }}>The director proposes</strong>
        <span className="sub-dim" style={{ fontSize: 12 }}>{here.length ? `${here.length} here` : 'nothing on this bench'}{there.length ? ` · ${there.length} elsewhere` : ''}</span>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn tiny" onClick={() => setOpen((o) => !o)}>{open ? 'hide' : 'show'}</button>
      </div>
      {open && memo && <p data-tool="director-memo" style={{ margin: 0, fontSize: 13, lineHeight: 1.45 }}>{memo.text}</p>}
      {open && (
        <div className="row wrap" style={{ gap: 6 }}>
          {[...here, ...there].map((p) => (
            <button key={p.id} type="button" className={`btn tiny${p.bench === view ? ' pri' : ''}`} title={p.why} onClick={() => onGo(p.bench, p.id)}
                    style={{ maxWidth: 360, whiteSpace: 'normal', textAlign: 'left' }}>
              {p.title}{p.bench !== view ? <span className="sub-dim"> → {p.action}</span> : <span className="sub-dim"> · {p.action}</span>}
            </button>
          ))}
        </div>
      )}
    </aside>
  );
}
